import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type Tone = 'info' | 'succes' | 'avertissement' | 'erreur'

const TONE_CLASSES: Record<Tone, string> = {
  info: 'border-bordure bg-carte text-texte',
  succes: 'border-vert bg-vert-clair text-vert',
  avertissement: 'border-terre bg-carte text-terre',
  erreur: 'border-rouge bg-carte text-rouge',
}

/**
 * Bandeau posé DANS la page, pour un message rattaché à ce qu'on regarde : une erreur de
 * formulaire, un avertissement sur un dépôt désactivé. À distinguer des notifications
 * flottantes ci-dessous, qui signalent l'issue d'une action et disparaissent.
 */
export function Alert({ tone = 'info', title, children }: { tone?: Tone; title?: string; children: ReactNode }) {
  return (
    <div
      role={tone === 'erreur' ? 'alert' : 'status'}
      className={`rounded-app border px-4 py-3 ${TONE_CLASSES[tone]}`}
    >
      {title !== undefined && <strong className="block">{title}</strong>}
      <div className={title !== undefined ? 'mt-1' : ''}>{children}</div>
    </div>
  )
}

export function Badge({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`inline-block rounded-full border px-[0.6rem] py-[0.1rem] text-[0.8rem] ${TONE_CLASSES[tone]}`}>
      {children}
    </span>
  )
}

export function Spinner({ label }: { label: string }) {
  return (
    <p className="text-texte-doux" role="status" aria-live="polite">
      {label}
    </p>
  )
}

interface Notification {
  id: number
  tone: Tone
  message: string
}

interface ToastValue {
  notify: (message: string, tone?: Tone) => void
}

const ToastContext = createContext<ToastValue | null>(null)

/** Durée d'affichage d'une notification, en millisecondes. */
const DUREE = 5000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const notify = useCallback((message: string, tone: Tone = 'info') => {
    setNotifications((current) => [...current, { id: Date.now() + current.length, tone, message }])
  }, [])

  // Un seul minuteur pour toute la pile, réarmé à chaque changement : autant de minuteurs
  // que de notifications finirait par en laisser un qui tourne après le démontage.
  useEffect(() => {
    if (notifications.length === 0) {
      return
    }

    const timer = window.setTimeout(() => setNotifications((current) => current.slice(1)), DUREE)

    return () => window.clearTimeout(timer)
  }, [notifications])

  const value = useMemo<ToastValue>(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* `inset-inline-end` et non `right` : en arabe, la pile se place à gauche.
          aria-live="polite" fait annoncer le message sans interrompre la lecture. */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 z-50 flex flex-col gap-2 [inset-inline-end:1rem]"
      >
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`pointer-events-auto max-w-[22rem] rounded-app border px-4 py-3 shadow-lg ${TONE_CLASSES[notification.tone]}`}
          >
            {notification.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext)

  if (context === null) {
    throw new Error('useToast doit être appelé à l’intérieur de <ToastProvider>.')
  }

  return context
}
